/* ------------------------------------------------------------------
   Sidebar – toolbar + fly-outs (Crop, Trim, Filters, Text, Audio)
   • Desktop / Tablet  (≥ 768 px) : fixed left rail (180 px)
   • Mobile            (< 768 px) : bottom icon bar + floating sub-panel
------------------------------------------------------------------ */
import React from 'react';
import PropTypes from 'prop-types';
import { Button, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  Crop, Scissors, Magic, MusicNote, Type,
} from 'react-bootstrap-icons';
import { PRESETS } from './constants';
import FiltersPanel from './FiltersPanel';
import TextPanel    from './TextPanel';
import useIsMobile  from '../../../../../hooks/useIsMobile';

/* layout constants */
const NAV_W_DESKTOP    = 180;
const FLYOUT_W_DESKTOP = 110;
const BAR_H            = 60;

/* ------------------------------------------------------------------ */
export default function Sidebar({
  active,
  onChange,
  /* crop */
  preset, setPreset,
  zoom,   setZoom,
  showApply, onApply,
  /* filters */
  filterId, setFilterId,
  filterLevel, setFilterLevel,
  filterDirty, onFilterApply,
  /* text props */
  textProps,
  /* layout */
  mobileHorizontal = false,      // set by VideoEditorModal on phones
}) {
  const isMobile  = useIsMobile();
  const isDesktop = !isMobile;

  /* tool definitions */
  const tools = [
    { id:'crop',    icon:<Crop size={24}/>,    label:'Crop'    },
    { id:'trim',    icon:<Scissors size={24}/>,label:'Trim'    },
    { id:'filters', icon:<Magic size={24}/>,   label:'Filters' },
    { id:'text',    icon:<Type size={24}/>,    label:'Text'    },
    { id:'audio',   icon:<MusicNote size={24}/>,label:'Audio'  },
  ];

  /* base styles */
  const navStyleDesktop   = { width:NAV_W_DESKTOP, height:'100%' };
  const flyoutStyleDesktop = {
    position:'absolute',
    top:0, bottom:0, left:'100%',
    width:(active==='filters'||active==='text') ? 260 : FLYOUT_W_DESKTOP,
    overflowY:'auto',
    background:'#fff',
    borderLeft:'1px solid #dee2e6',
    zIndex:2,
  };

  /* mobile floating panel wrapper */
  const SubPanel = ({ children }) => (
    <div
      className={isDesktop ? '' : 'video-editor-mobile-flyout'}
      style={isDesktop ? flyoutStyleDesktop : {}}
    >
      {children}
    </div>
  );

  /* ------------------------------------------------------------------
     Render – MOBILE HORIZONTAL MODE
  ------------------------------------------------------------------ */
  if (mobileHorizontal) {
    return (
      <>
        {/* ---------- bottom icon bar ---------- */}
        <div className="video-editor-bottom-bar">
          {tools.map(t => (
            <SidebarButton
              key={t.id}
              icon={t.icon}
              label={t.label}
              active={active===t.id}
              onClick={()=>onChange(t.id)}
            />
          ))}
        </div>

        {/* ---------- sub-panels (mobile) ---------- */}
        {active==='crop' && (
          <SubPanel>
            {/* swipe-able preset chips */}
            <div className="d-flex overflow-auto px-2 py-2" style={{ gap:8 }}>
              {PRESETS.map(p => (
                <Button
                  key={p.id}
                  variant={preset===p.id ? 'primary':'light'}
                  className="flex-shrink-0 text-center"
                  style={{ minWidth:70 }}
                  size="sm"
                  onClick={()=>setPreset(p.id)}
                >
                  {p.label}
                </Button>
              ))}
            </div>

            <div className="px-2 py-3 border-top small">
              <Form.Label className="mb-1">Zoom</Form.Label>
              <Form.Range
                min={1} max={3} step={0.05}
                value={zoom}
                onChange={e=>setZoom(parseFloat(e.target.value))}
              />
            </div>

            {showApply && (
              <Button
                variant="primary"
                className="mx-2 mb-3"
                size="sm"
                onClick={onApply}
              >
                Apply Crop
              </Button>
            )}
          </SubPanel>
        )}

        {active==='filters' && (
          <SubPanel>
            <FiltersPanel
              currentId     ={filterId}
              currentLevel  ={filterLevel}
              onSelect      ={setFilterId}
              onIntensity   ={setFilterLevel}
              dirty         ={filterDirty}
              onApply       ={onFilterApply}
            />
          </SubPanel>
        )}

        {active==='text' && (
          <SubPanel>
            <TextPanel {...textProps}/>
          </SubPanel>
        )}
      </>
    );
  }

  /* ------------------------------------------------------------------
     Render – DESKTOP / TABLET
  ------------------------------------------------------------------ */
  return (
    <div className="d-flex position-relative">
      {/* ---------- left rail ---------- */}
      <nav className="d-flex flex-column border-end" style={navStyleDesktop}>
        {tools.map(t => (
          <SidebarButton
            key={t.id}
            icon={t.icon}
            label={t.label}
            active={active===t.id}
            onClick={()=>onChange(t.id)}
          />
        ))}
      </nav>

      {/* ---------- sub-panels (desktop) ---------- */}
      {active==='crop' && (
        <SubPanel>
          {PRESETS.map(p=>(
            <Button
              key={p.id}
              variant={preset===p.id ? 'primary':'light'}
              className="w-100 rounded-0 text-center py-2"
              onClick={()=>setPreset(p.id)}
              size="sm"
            >
              {p.label}
            </Button>
          ))}

          <div className="px-2 py-3 border-top small">
            <Form.Label className="mb-1">Zoom</Form.Label>
            <Form.Range
              min={1} max={3} step={0.05}
              value={zoom}
              onChange={e=>setZoom(parseFloat(e.target.value))}
            />
          </div>

          {showApply && (
            <Button
              variant="primary"
              className="mx-2 mb-3"
              size="sm"
              onClick={onApply}
            >
              Apply Crop
            </Button>
          )}
        </SubPanel>
      )}

      {active==='filters' && (
        <SubPanel>
          <FiltersPanel
            currentId     ={filterId}
            currentLevel  ={filterLevel}
            onSelect      ={setFilterId}
            onIntensity   ={setFilterLevel}
            dirty         ={filterDirty}
            onApply       ={onFilterApply}
          />
        </SubPanel>
      )}

      {active==='text' && (
        <SubPanel>
          <TextPanel {...textProps}/>
        </SubPanel>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Little icon-and-label button                                      */
function SidebarButton({ icon,label,active,onClick }) {
  const node = (
    <div
      className={`sidebar-button d-flex flex-column align-items-center justify-content-center h-100 ${
        active ? 'bg-white border-start border-4 border-primary' : ''
      }`}
      style={{ cursor:'pointer' }}
      onClick={onClick}
    >
      {icon}
      <span className="small mt-1 text-muted">{label}</span>
    </div>
  );
  return (
    <OverlayTrigger
      placement="right"
      delay={{ show:250, hide:0 }}
      overlay={<Tooltip>{label}</Tooltip>}
    >
      {node}
    </OverlayTrigger>
  );
}

/* ------------------------------------------------------------------ */
/*  PropTypes                                                         */
Sidebar.propTypes = {
  active          : PropTypes.string,
  onChange        : PropTypes.func,
  /* crop */
  preset          : PropTypes.string,
  setPreset       : PropTypes.func,
  zoom            : PropTypes.number,
  setZoom         : PropTypes.func,
  showApply       : PropTypes.bool,
  onApply         : PropTypes.func,
  /* filters */
  filterId        : PropTypes.string,
  setFilterId     : PropTypes.func,
  filterLevel     : PropTypes.number,
  setFilterLevel  : PropTypes.func,
  filterDirty     : PropTypes.bool,
  onFilterApply   : PropTypes.func,
  /* text */
  textProps       : PropTypes.object,
  /* layout */
  mobileHorizontal: PropTypes.bool,
};
